import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LinkButton } from "../components/Button";
import { apiFetch, resolveAssetUrl } from "../lib/api";
import printerImage from "../assets/home-orbit/printer.png";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

type AboutConfig = {
  story_text?: string;
  how_it_works_steps?: string[];
  why_choose_us?: string[];
  stl_file_url?: string;
  what_we_do_title?: string;
  what_we_do_text?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_instagram?: string;
  map_embed_url?: string;
};

export function AboutPage() {
  const [data, setData] = useState<AboutConfig>({});

  useEffect(() => {
    let cancelled = false;
    apiFetch<AboutConfig>("/about-us")
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch(() => {
        if (!cancelled) setData({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const steps = data.how_it_works_steps || ["Upload STL File", "Select Material & Get Quote", "Fast Local Delivery"];
  const bullets = data.why_choose_us || ["Fast Turnaround", "Quality FDM Prints", "Affordable Pricing"];
  const email = data.contact_email || "support@protodeccan.com";
  const phone = data.contact_phone || "+91 6360164984";
  const instagramHandle = typeof data.contact_instagram === "string" ? data.contact_instagram.trim() : "@protodeccan";
  const showInstagram = Boolean(instagramHandle);
  const cleanInstagram = instagramHandle.replace(/^@/, "");
  const whatWeDoTitle = data.what_we_do_title || "Custom FDM 3D Printing On Demand";
  const whatWeDoHtml = simpleMarkdown(
    data.what_we_do_text || "Upload your 3D Model, select your material and finish, and we will print it for you."
  );

  return (
    <div className="about-page">
      <div className="about-bento">
        <div className="about-panel about-story-panel">
          <div className="text-upper text-accent mb-md" style={{ fontSize: "0.88rem" }}>What We Do</div>
          <h3 id="about-what-title" style={{ marginBottom: "var(--space-sm)" }}>{whatWeDoTitle}</h3>
          <div
            id="about-what-text"
            className="about-what-copy"
            style={{ marginBottom: "var(--space-md)" }}
            dangerouslySetInnerHTML={{ __html: whatWeDoHtml }}
          />
          <div className="about-model-frame">
            {data.stl_file_url ? (
              <Suspense fallback={<div className="about-model-loading" aria-hidden="true" />}>
                <STLViewer url={resolveAssetUrl(data.stl_file_url)} mode="product" interactive autoRotate hideLoadingOverlay />
              </Suspense>
            ) : (
              <div className="about-model-fallback" aria-hidden="true">
                <img src={printerImage} alt="" />
              </div>
            )}
          </div>
        </div>

        <div className="about-panel about-process-panel">
          <div className="about-process-inner">
            <div className="text-upper text-accent mb-sm" style={{ fontSize: "0.88rem" }}>How It Works</div>
            <ol id="about-steps-list" className="about-steps" style={{ marginBottom: 0 }}>
              {steps.map((step) => <li key={step}><span>{step}</span></li>)}
            </ol>
          </div>
          <div className="about-process-footer">
            <span className="about-process-label text-upper text-accent">Have questions?</span>
            <Link
              to="/faq"
              className="btn btn-secondary btn-sm"
              style={{
                fontSize: "0.82rem",
                padding: "6px 14px",
                borderRadius: "8px",
                borderColor: "var(--border-strong)",
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              Read our FAQ
            </Link>
          </div>
        </div>

        <div className="about-panel about-cta-panel">
          <div className="text-upper text-accent mb-md" style={{ fontSize: "0.88rem" }}>Why Choose Us</div>
          <ul id="about-why-list" className="about-bullets" style={{ marginBottom: "var(--space-md)" }}>
            {bullets.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <div className="about-cta-actions">
            <LinkButton to="/quote">Get a Quote</LinkButton>
            <LinkButton to="/products" variant="secondary">Browse Catalog</LinkButton>
          </div>
          <div className="about-cta-divider" />
          <div className="text-upper text-accent mb-sm" style={{ fontSize: "0.88rem" }}>Contact Us</div>
          <div className="about-contact-row">
            <div className="about-contact-item">
              <span className="contact-label"><MailIcon /></span>
              <a href={`mailto:${email}`} id="about-contact-email">{email}</a>
            </div>
            <div className="about-contact-item">
              <span className="contact-label"><PhoneIcon /></span>
              <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} id="about-contact-phone">{phone}</a>
            </div>
            <div
              className="about-contact-item"
              id="about-instagram-row"
              style={showInstagram ? undefined : { display: "none" }}
            >
              <span className="contact-label"><InstagramIcon /></span>
              <a href={`https://instagram.com/${cleanInstagram}`} id="about-contact-instagram" target="_blank" rel="noopener noreferrer">
                {cleanInstagram}
              </a>
            </div>
          </div>
        </div>

        <div className="about-panel about-map-panel">
          {data.map_embed_url ? (
            <div className="map-container">
              <iframe id="about-map-iframe" src={data.map_embed_url} loading="lazy" title="ProtoDeccan location map" />
            </div>
          ) : (
            <div className="about-map-fallback">
              <span>Marathahalli</span>
              <strong>Bengaluru workshop</strong>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function simpleMarkdown(input: string) {
  const escaped = escapeHtml(input || "");
  return escaped
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

function escapeHtml(input: string) {
  return input
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function MailIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.38028 8.85335C9.07627 10.303 10.0251 11.6616 11.2266 12.8632C12.4282 14.0648 13.7869 15.0136 15.2365 15.7096C15.3612 15.7694 15.4235 15.7994 15.5024 15.8224C15.7828 15.9041 16.127 15.8454 16.3644 15.6754C16.4313 15.6275 16.4884 15.5704 16.6027 15.4561C16.9523 15.1064 17.1271 14.9316 17.3029 14.8174C17.9658 14.3864 18.8204 14.3864 19.4833 14.8174C19.6591 14.9316 19.8339 15.1064 20.1835 15.4561L20.3783 15.6509C20.9098 16.1824 21.1755 16.4481 21.3198 16.7335C21.6069 17.301 21.6069 17.9713 21.3198 18.5389C21.1755 18.8242 20.9098 19.09 20.3783 19.6214L20.2207 19.779C19.6911 20.3087 19.4263 20.5735 19.0662 20.7757C18.6667 21.0001 18.0462 21.1615 17.588 21.1601C17.1751 21.1589 16.8928 21.0788 16.3284 20.9186C13.295 20.0576 10.4326 18.4332 8.04466 16.0452C5.65668 13.6572 4.03221 10.7948 3.17124 7.76144C3.01103 7.19699 2.93092 6.91477 2.9297 6.50182C2.92833 6.0436 3.08969 5.42311 3.31411 5.0236C3.51636 4.66357 3.78117 4.39876 4.3108 3.86913L4.46843 3.7115C4.99987 3.18006 5.2656 2.91433 5.55098 2.76999C6.11854 2.48292 6.7888 2.48292 7.35636 2.76999C7.64174 2.91433 7.90747 3.18006 8.43891 3.7115L8.63378 3.90637C8.98338 4.25597 9.15819 4.43078 9.27247 4.60655C9.70347 5.26945 9.70347 6.12403 9.27247 6.78692C9.15819 6.96269 8.98338 7.1375 8.63378 7.4871C8.51947 7.60142 8.46231 7.65857 8.41447 7.72538C8.24446 7.96281 8.18576 8.30707 8.26748 8.58743C8.29048 8.66632 8.32041 8.72866 8.38028 8.85335Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 3364.7 3364.7" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="instagram-grad-react-0" cx="217.76" cy="3290.99" r="4271.92" gradientUnits="userSpaceOnUse">
          <stop offset=".09" stopColor="#fa8f21" />
          <stop offset=".78" stopColor="#d82d7e" />
        </radialGradient>
        <radialGradient id="instagram-grad-react-1" cx="2330.61" cy="3182.95" r="3759.33" gradientUnits="userSpaceOnUse">
          <stop offset=".64" stopColor="#8c3aaa" stopOpacity="0" />
          <stop offset="1" stopColor="#8c3aaa" />
        </radialGradient>
      </defs>
      <path d="M853.2,3352.8c-200.1-9.1-308.8-42.4-381.1-70.6-95.8-37.3-164.1-81.7-236-153.5S119.7,2988.6,82.6,2892.8c-28.2-72.3-61.5-181-70.6-381.1C2,2295.4,0,2230.5,0,1682.5s2.2-612.8,11.9-829.3C21,653.1,54.5,544.6,82.5,472.1,119.8,376.3,164.3,308,236,236c71.8-71.8,140.1-116.4,236-153.5C544.3,54.3,653,21,853.1,11.9,1069.5,2,1134.5,0,1682.3,0c548,0,612.8,2.2,829.3,11.9,200.1,9.1,308.6,42.6,381.1,70.6,95.8,37.1,164.1,81.7,236,153.5s116.2,140.2,153.5,236c28.2,72.3,61.5,181,70.6,381.1,9.9,216.5,11.9,281.3,11.9,829.3,0,547.8-2,612.8-11.9,829.3-9.1,200.1-42.6,308.8-70.6,381.1-37.3,95.8-81.7,164.1-153.5,235.9s-140.2,116.2-236,153.5c-72.3,28.2-181,61.5-381.1,70.6-216.3,9.9-281.3,11.9-829.3,11.9-547.8,0-612.8-1.9-829.1-11.9" fill="url(#instagram-grad-react-0)" />
      <path d="M853.2,3352.8c-200.1-9.1-308.8-42.4-381.1-70.6-95.8-37.3-164.1-81.7-236-153.5S119.7,2988.6,82.6,2892.8c-28.2-72.3-61.5-181-70.6-381.1C2,2295.4,0,2230.5,0,1682.5s2.2-612.8,11.9-829.3C21,653.1,54.5,544.6,82.5,472.1,119.8,376.3,164.3,308,236,236c71.8-71.8,140.1-116.4,236-153.5C544.3,54.3,653,21,853.1,11.9,1069.5,2,1134.5,0,1682.3,0c548,0,612.8,2.2,829.3,11.9,200.1,9.1,308.6,42.6,381.1,70.6,95.8,37.1,164.1,81.7,236,153.5s116.2,140.2,153.5,236c28.2,72.3,61.5,181,70.6,381.1,9.9,216.5,11.9,281.3,11.9,829.3,0,547.8-2,612.8-11.9,829.3-9.1,200.1-42.6,308.8-70.6,381.1-37.3,95.8-81.7,164.1-153.5,235.9s-140.2,116.2-236,153.5c-72.3,28.2-181,61.5-381.1,70.6-216.3,9.9-281.3,11.9-829.3,11.9-547.8,0-612.8-1.9-829.1-11.9" fill="url(#instagram-grad-react-1)" />
      <path d="M1269.25,1689.52c0-230.11,186.49-416.7,416.6-416.7s416.7,186.59,416.7,416.7-186.59,416.7-416.7,416.7-416.6-186.59-416.6-416.7m-225.26,0c0,354.5,287.36,641.86,641.86,641.86s641.86-287.36,641.86-641.86-287.36-641.86-641.86-641.86S1044,1335,1044,1689.52m1159.13-667.31a150,150,0,1,0,150.06-149.94h-0.06a150.07,150.07,0,0,0-150,149.94M1180.85,2707c-121.87-5.55-188.11-25.85-232.13-43-58.36-22.72-100-49.78-143.78-93.5s-70.88-85.32-93.5-143.68c-17.16-44-37.46-110.26-43-232.13-6.06-131.76-7.27-171.34-7.27-505.15s1.31-373.28,7.27-505.15c5.55-121.87,26-188,43-232.13,22.72-58.36,49.78-100,93.5-143.78s85.32-70.88,143.78-93.5c44-17.16,110.26-37.46,232.13-43,131.76-6.06,171.34-7.27,505-7.27S2059.13,666,2191,672c121.87,5.55,188,26,232.13,43,58.36,22.62,100,49.78,143.78,93.5s70.78,85.42,93.5,143.78c17.16,44,37.46,110.26,43,232.13,6.06,131.87,7.27,171.34,7.27,505.15s-1.21,373.28-7.27,505.15c-5.55,121.87-25.95,188.11-43,232.13-22.72,58.36-49.78,100-93.5,143.68s-85.42,70.78-143.78,93.5c-44,17.16-110.26,37.46-232.13,43-131.76,6.06-171.34,7.27-505.15,7.27s-373.28-1.21-505-7.27M1170.5,447.09c-133.07,6.06-224,27.16-303.41,58.06-82.19,31.91-151.86,74.72-221.43,144.18S533.39,788.47,501.48,870.76c-30.9,79.46-52,170.34-58.06,303.41-6.16,133.28-7.57,175.89-7.57,515.35s1.41,382.07,7.57,515.35c6.06,133.08,27.16,223.95,58.06,303.41,31.91,82.19,74.62,152,144.18,221.43s139.14,112.18,221.43,144.18c79.56,30.9,170.34,52,303.41,58.06,133.35,6.06,175.89,7.57,515.35,7.57s382.07-1.41,515.35-7.57c133.08-6.06,223.95-27.16,303.41-58.06,82.19-32,151.86-74.72,221.43-144.18s112.18-139.24,144.18-221.43c30.9-79.46,52.1-170.34,58.06-303.41,6.06-133.38,7.47-175.89,7.47-515.35s-1.41-382.07-7.47-515.35c-6.06-133.08-27.16-224-58.06-303.41-32-82.19-74.72-151.86-144.18-221.43S2586.8,537.06,2504.71,505.15c-79.56-30.9-170.44-52.1-303.41-58.06C2068,441,2025.41,439.52,1686,439.52s-382.1,1.41-515.45,7.57" fill="#ffffff" />
    </svg>
  );
}
